'use client';

import { useEffect } from 'react';
import { trackPushNotificationClicked } from '@/app/lib/analytics-events';

// Counts arrivals from a push notification. The service worker resolves a click in one of two ways
// (see public/sw.js), so this listens for both: it either navigates to a URL carrying a transient
// `?push_click=<campaign>` marker, which this strips once counted, or — when a tab is already on the
// target page — it only focuses that tab and posts a message, with no navigation and no params.
//
// The landing URL also carries utm_source=push, which is what actually attributes the session in
// PostHog and GA; those params are deliberately left in place.
export function PushClickTracker() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaign = params.get('push_click');

    // Deferred rather than fired inline: this effect runs before PHProvider's (React commits child
    // effects first), so an immediate capture would land before posthog.init and be dropped.
    const timeout = campaign ? setTimeout(() => trackPushNotificationClicked(campaign, false), 1000) : undefined;

    if (campaign) {
      params.delete('push_click');
      const search = params.toString();
      const url = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
      window.history.replaceState(null, '', url);
    }

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'push-click') {
        trackPushNotificationClicked(event.data.campaign ?? 'unknown', true);
      }
    };

    const hasServiceWorker = 'serviceWorker' in navigator;
    if (hasServiceWorker) navigator.serviceWorker.addEventListener('message', onMessage);

    return () => {
      if (timeout) clearTimeout(timeout);
      if (hasServiceWorker) navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, []);

  return null;
}
